export interface SphereGeometry {
  positions: Float32Array;
  normals: Float32Array;
  indices: Uint16Array;
}

export interface QuadGeometry {
  positions: Float32Array;
  uvs: Float32Array;
}

/**
 * Generates an icosphere by recursively subdividing an icosahedron.
 * Subdivision 0 = 12 vertices, 1 = 42, 2 = 162, 3 = 642.
 * All vertices lie on a unit sphere (radius 1). Normals = positions.
 */
export function createIcoSphere(subdivisions: number): SphereGeometry {
  const t = (1.0 + Math.sqrt(5.0)) / 2.0;

  const verts: number[] = [
    -1,  t,  0,   1,  t,  0,  -1, -t,  0,   1, -t,  0,
     0, -1,  t,   0,  1,  t,   0, -1, -t,   0,  1, -t,
     t,  0, -1,   t,  0,  1,  -t,  0, -1,  -t,  0,  1,
  ];

  let tris = [
    0,11,5,  0,5,1,   0,1,7,   0,7,10,  0,10,11,
    1,5,9,   5,11,4,  11,10,2, 10,7,6,  7,1,8,
    3,9,4,   3,4,2,   3,2,6,   3,6,8,   3,8,9,
    4,9,5,   2,4,11,  6,2,10,  8,6,7,   9,8,1,
  ];

  const midCache = new Map<number, number>();

  for (let s = 0; s < subdivisions; s++) {
    const next: number[] = [];
    for (let i = 0; i < tris.length; i += 3) {
      const a = tris[i], b = tris[i + 1], c = tris[i + 2];
      const ab = getMidpoint(a, b, verts, midCache);
      const bc = getMidpoint(b, c, verts, midCache);
      const ca = getMidpoint(c, a, verts, midCache);
      next.push(
        a, ab, ca,  ab, b, bc,  ca, bc, c,  ab, bc, ca,
      );
    }
    tris = next;
  }

  const count = verts.length / 3;
  const positions = new Float32Array(verts.length);
  const normals = new Float32Array(verts.length);

  for (let i = 0; i < count; i++) {
    const i3 = i * 3;
    const x = verts[i3], y = verts[i3 + 1], z = verts[i3 + 2];
    const len = Math.sqrt(x * x + y * y + z * z);
    const nx = x / len, ny = y / len, nz = z / len;
    positions[i3] = nx;     positions[i3 + 1] = ny;     positions[i3 + 2] = nz;
    normals[i3] = nx;       normals[i3 + 1] = ny;       normals[i3 + 2] = nz;
  }

  return { positions, normals, indices: new Uint16Array(tris) };
}

function getMidpoint(
  a: number, b: number,
  verts: number[],
  cache: Map<number, number>,
): number {
  const key = a < b ? a * 65536 + b : b * 65536 + a;
  const cached = cache.get(key);
  if (cached !== undefined) return cached;

  const a3 = a * 3, b3 = b * 3;
  const mx = (verts[a3] + verts[b3]) / 2;
  const my = (verts[a3 + 1] + verts[b3 + 1]) / 2;
  const mz = (verts[a3 + 2] + verts[b3 + 2]) / 2;

  const idx = verts.length / 3;
  verts.push(mx, my, mz);
  cache.set(key, idx);
  return idx;
}

/**
 * Billboard quad from -1 to 1 (two triangles, 6 vertices).
 * Positions are 2D corners, UVs match for fragment shader.
 */
export function createQuad(): QuadGeometry {
  return {
    positions: new Float32Array([
      -1, -1,   1, -1,   1, 1,
      -1, -1,   1,  1,  -1, 1,
    ]),
    uvs: new Float32Array([
      -1, -1,   1, -1,   1, 1,
      -1, -1,   1,  1,  -1, 1,
    ]),
  };
}
